import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const styles = {
  card: {
    border: 0,
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "9px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 12, fontSize: 11 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  alert: { fontSize: 11, padding: "7px 10px", marginBottom: 8 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  badge: { fontSize: 10, padding: "4px 7px", borderRadius: 20 },
};

export default function CompanySetupPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const empty = {
    id: 0,
    companyName: "",
    gstin: "",
    pan: "",
    address: "",
    phone: "",
    email: "",
    stateCode: "",
    logoUrl: "",
    isActive: true,
  };

  const [form, setForm] = useState(empty);
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState(location.state?.setupMessage || "");
  const [error, setError] = useState("");

  const text = (v) =>
    v === null || v === undefined || typeof v === "object" ? "" : String(v);

  const boolValue = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") return v.toLowerCase() === "true" || v === "1";
    return true;
  };

  const normalizeCompany = (r) => ({
    id: r.id ?? r.Id ?? 0,
    companyName: text(r.companyName ?? r.CompanyName),
    gstin: text(r.gstin ?? r.GSTIN),
    pan: text(r.pan ?? r.PAN),
    address: text(r.address ?? r.Address),
    phone: text(r.phone ?? r.Phone),
    email: text(r.email ?? r.Email),
    stateCode: text(r.stateCode ?? r.StateCode),
    logoUrl: text(r.logoUrl ?? r.LogoUrl),
    isActive: boolValue(r.isActive ?? r.IsActive),
  });

  const load = async () => {
    try {
      setError("");
      const res = await client.get("/companies", { skipAuthRedirect: true });
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data?.rows)
            ? res.data.rows
            : [];
      setRows(data.map(normalizeCompany));
    } catch {
      setRows([]);
      setError("Failed to load companies");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!form.companyName.trim()) return setError("Company name is required");

    try {
      const payload = {
        id: Number(form.id || 0),
        companyName: form.companyName.trim(),
        gstin: form.gstin.trim(),
        pan: form.pan.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        stateCode: form.stateCode.trim(),
        logoUrl: form.logoUrl.trim(),
        isActive: form.isActive,
      };

      const res = await client.post("/companies", payload, {
        skipAuthRedirect: true,
      });
      const savedId = res.data?.id ?? res.data?.Id ?? payload.id;

      const userCompany = res.data?.userCompany || {};
      if (savedId && refreshUser) {
        refreshUser({
          companyId: savedId,
          companyName: userCompany.companyName || payload.companyName,
          companyAddress: userCompany.companyAddress || payload.address,
          companyGstin: userCompany.companyGstin || payload.gstin,
          companyPhone: userCompany.companyPhone || payload.phone,
        });
      }

      setForm(empty);
      setMessage(
        user?.id
          ? "Company saved and linked to your login successfully."
          : "Company saved successfully. Please login.",
      );
      await load();
      if (!user?.id) {
        navigate("/login", { replace: true });
      }
    } catch (err) {
      setError(err.response?.data || "Failed to save company");
    }
  };

  const edit = (r) => {
    setForm(normalizeCompany(r));
    setMessage("");
    setError("");
  };

  const assignToMe = async (r) => {
    setMessage("");
    setError("");
    try {
      const companyId = r.id || r.Id;
      const res = await client.post(
        `/companies/${companyId}/assign-current-user`,
      );
      if (refreshUser) {
        refreshUser({
          companyId: res.data.companyId,
          companyName: res.data.companyName,
          companyAddress: res.data.companyAddress,
          companyGstin: res.data.companyGstin,
          companyPhone: res.data.companyPhone,
        });
      }
      setMessage("Company linked to your login successfully.");
    } catch (err) {
      setError(err.response?.data || "Failed to link company to your login");
    }
  };

  const clear = () => {
    setForm(empty);
    setMessage("");
    setError("");
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h6 style={styles.title}>
          <i className="bi bi-buildings me-2"></i>
          Company Setup
        </h6>
        <div style={styles.sub}>
          Manage company profile, GST, PAN and status
        </div>
      </div>

      <div style={styles.body}>
        {!user?.companyId && (
          <div className="alert alert-warning" style={styles.alert}>
            Please create your company details first. ERP modules will open
            after company setup.
          </div>
        )}

        {message && (
          <div className="alert alert-info" style={styles.alert}>
            {message}
          </div>
        )}
        {error && (
          <div className="alert alert-danger" style={styles.alert}>
            {text(error)}
          </div>
        )}

        <form onSubmit={save} className="row g-2">
          {[
            ["companyName", "Company Name *", "col-md-4"],
            ["gstin", "GSTIN", "col-md-2"],
            ["pan", "PAN", "col-md-2"],
            ["stateCode", "State Code", "col-md-2"],
            ["phone", "Phone", "col-md-2"],
            ["email", "Email", "col-md-3"],
            ["address", "Address", "col-md-4"],
            ["logoUrl", "Logo URL", "col-md-3"],
          ].map(([key, label, col]) => (
            <div className={col} key={key}>
              <label style={styles.label}>{label}</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form[key]}
                onChange={(e) => set(key, e.target.value)}
              />
            </div>
          ))}

          <div className="col-md-2">
            <label style={styles.label}>Status</label>
            <select
              className="form-select form-select-sm"
              style={styles.input}
              value={form.isActive ? "true" : "false"}
              onChange={(e) => set("isActive", e.target.value === "true")}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <button className="btn btn-primary btn-sm w-100" style={styles.btn}>
              <i className="bi bi-check2-circle me-1"></i>
              {form.id ? "Update" : "Save"}
            </button>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100"
              style={styles.btn}
              onClick={clear}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear
            </button>
          </div>
        </form>

        <div style={{ height: 1, background: "#e9ecef", margin: "12px 0" }} />

        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-hover align-middle mb-0">
            <thead>
              <tr>
                {["Name", "GSTIN", "PAN", "State", "Phone", "Status", ""].map(
                  (h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center text-muted"
                    style={{ ...styles.td, padding: 18 }}
                  >
                    No companies found
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.id || i}>
                    <td style={styles.td}>{text(r.companyName)}</td>
                    <td style={styles.td}>{text(r.gstin)}</td>
                    <td style={styles.td}>{text(r.pan)}</td>
                    <td style={styles.td}>{text(r.stateCode)}</td>
                    <td style={styles.td}>{text(r.phone)}</td>
                    <td style={styles.td}>
                      <span
                        className={
                          r.isActive ? "badge bg-success" : "badge bg-secondary"
                        }
                        style={styles.badge}
                      >
                        {r.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          style={{
                            ...styles.btn,
                            height: 27,
                            padding: "2px 9px",
                          }}
                          onClick={() => edit(r)}
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          style={{
                            ...styles.btn,
                            height: 27,
                            padding: "2px 9px",
                          }}
                          onClick={() => assignToMe(r)}
                        >
                          <i className="bi bi-link-45deg me-1"></i>
                          Use
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
